import React, { Component } from "react";
import axios from "axios";
import { message, Tooltip } from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import ModalItem from "../../../../../Components/ModalItem";
import ButtonItem from "../../../../../Components/ButtonItem";
import InputNumberItem from "../../../../../Components/InputNumberItem";
import dineroDisabledMask from "../../../../../Components/dineroDisabledMask";
import round2Decimals from "../../../../../Components/round2Decimals";
import EtiquetaMoneda from "../../../../../Components/EtiquetaMoneda";
import TarjetaPrecio from "./TarjetaPrecio";
import HistorialCostos from "./HistorialCostos";
import {
  calcularImpuestosSobreBases,
  precioDesdePorcentaje,
  etiquetaImpuesto,
  nombreImpuesto,
  construirFilasDePrecios,
} from "./verInsumo.utils";
import { normalizarAtajosPorcentaje } from "../../../../../Utils/reglasPrecio";

// Modal de consulta de un insumo desde el renglon de un documento de venta. Lo abre el CLIC
// DERECHO en la columna PU (ver DisponibilidadInsumoModal / HistorialInsumoClienteModal, sus
// hermanos en las otras celdas del mismo renglon).
//
// Contrato de cierre: `cerrarVerInsumo(precio?)`. Cerrar normal llama sin argumento; elegir un
// precio lo llama con el importe SIN impuestos, que es lo que espera precioUnitario. Lo mismo
// para las 10 pantallas que lo montan, todas con los mismos 6 props.
//
// `id` puede ser el _id de un Insumo o el de un Insumo.equivalencias[]: el endpoint
// datosVisualInsumo resuelve los dos y, en el caso de la equivalencia, devuelve el costo y el
// historial ya divididos entre el factor.

const COLOR_MARCA = "#00BFD6";
const COLOR_HISTORICO = "#722ed1";

class VerInsumo extends Component {
  state = {
    // Identidad
    descripcion: "",
    descripcionLarga: "",
    clave: "",
    unidad: "",
    esEquivalencia: false,
    factorEquivalencia: 1,
    descripcionPrincipal: "",
    unidadPrincipal: "",

    // Costo
    costo: 0,
    costoOriginal: 0,
    costoImpuestos: 0,
    fechaCostoSeleccionado: null,
    // Abreviacion de Insumo.monedaId: el costo, los precios y el historial estan en ESA
    // moneda, no necesariamente en pesos. Solo se rotula, no se convierte nada.
    moneda: "MXN",

    // Configuracion del insumo / empresa
    impuestos: [],
    calcularPreciosSobre: false, // true: % sobre costo | false: % sobre venta
    atajosPorcentaje: [], // atajos de la calculadora; vacio = los de por defecto
    preciosInsumo: [], // precios[] del insumo o de la equivalencia
    listaDePrecios: [], // listas de precios de la empresa

    // Calculadora
    porcentaje: 0,
    precioCalculado: 0,
    precioCalculadoConImpuestos: 0,

    // Historial
    historialCostos: [],
    verHistorialCostos: false,

    cargandoInsumo: false,
    cargandoPrecios: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible !== prevProps.visible && this.props.visible) {
      this.consultarInsumo();
      this.consultarPrecios();
    }
  }

  // ---------------------------------------------------------------- datos

  consultarInsumo = () => {
    this.setState({ cargandoInsumo: true });
    axios
      .post(
        "insumos/datosVisualInsumo",
        {
          empresaId: this.props.empresaId,
          insumoId: this.props.id,
          precioId: this.props.precioId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2 || !res.data.data?.descripcion) {
          message.error("Insumo no encontrado");
          this.setState({ cargandoInsumo: false });
          return;
        }

        const {
          descripcion = "",
          descripcionLarga = "",
          clave = "",
          costo = 0,
          impuestos = [],
          calcularPreciosSobre = false,
          atajosPorcentaje = [],
          porcentajeLista = 0,
          precios = [],
          historialCostos,
          unidad,
          unidadNombre,
          esEquivalencia = false,
          factorEquivalencia = 1,
          descripcionPrincipal = "",
          unidadPrincipal = "",
          abreviacionMoneda = "MXN",
        } = res.data.data;

        const totales = calcularImpuestosSobreBases(
          0,
          costo,
          impuestos,
          this.props.tipoDeCambio
        );

        const historial = (Array.isArray(historialCostos) ? [...historialCostos] : []).sort(
          (a, b) => new Date(b.fecha) - new Date(a.fecha)
        );

        let unidadSafe = "N/A";
        if (typeof unidad === "string") unidadSafe = unidad;
        else if (unidad && unidad.nombre) unidadSafe = unidad.nombre;
        else if (unidadNombre) unidadSafe = unidadNombre;

        this.setState(
          {
            descripcion,
            descripcionLarga,
            clave,
            unidad: unidadSafe,
            esEquivalencia,
            factorEquivalencia,
            descripcionPrincipal,
            unidadPrincipal,

            costo,
            costoOriginal: costo,
            costoImpuestos: totales.costoConImpuestos,
            fechaCostoSeleccionado: null,
            moneda: abreviacionMoneda,

            impuestos,
            calcularPreciosSobre,
            atajosPorcentaje,
            preciosInsumo: Array.isArray(precios) ? precios : [],

            porcentaje: Number(porcentajeLista || 0),

            historialCostos: historial,
            verHistorialCostos: false,
            cargandoInsumo: false,
          },
          this.recalcularTodo
        );
      })
      .catch(() => this.setState({ cargandoInsumo: false }));
  };

  consultarPrecios = () => {
    this.setState({ cargandoPrecios: true });
    axios
      .post(
        "empresas/selectPrecios",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        // selectPrecios responde 200 con {status:404} cuando la empresa no tiene listas
        const listas = Array.isArray(res.data) ? res.data : [];
        this.setState({ listaDePrecios: listas, cargandoPrecios: false });
      })
      .catch(() => {
        message.error("Error al obtener los precios");
        this.setState({ cargandoPrecios: false });
      });
  };

  // ----------------------------------------------------------- calculadora

  recalcularTodo = () => {
    const { calcularPreciosSobre, porcentaje, costo, impuestos } = this.state;

    const precioCalculado = precioDesdePorcentaje(
      costo,
      porcentaje,
      calcularPreciosSobre
    );

    const { precioConImpuestos } = calcularImpuestosSobreBases(
      precioCalculado,
      0,
      impuestos,
      this.props.tipoDeCambio
    );

    this.setState({
      precioCalculado,
      precioCalculadoConImpuestos: precioConImpuestos,
    });
  };

  onChangePorcentaje = (value) => {
    this.setState({ porcentaje: Number(value || 0) }, this.recalcularTodo);
  };

  cargarSugeridoEnCalculadora = (porcentajeObjetivo) => {
    this.setState({ porcentaje: Number(porcentajeObjetivo || 0) }, () => {
      this.recalcularTodo();
      message.success(`Margen del ${porcentajeObjetivo}% cargado en la calculadora`);
    });
  };

  usarCostoHistorial = (itemHistorial) => {
    const nuevoCosto = Number(itemHistorial.costo) || 0;
    const totales = calcularImpuestosSobreBases(
      0,
      nuevoCosto,
      this.state.impuestos,
      this.props.tipoDeCambio
    );

    this.setState(
      {
        costo: nuevoCosto,
        costoImpuestos: totales.costoConImpuestos,
        fechaCostoSeleccionado: itemHistorial.fecha,
      },
      () => {
        this.recalcularTodo();
        message.success("Calculadora simulando con el costo historico");
      }
    );
  };

  restaurarCostoOriginal = () => {
    const { costoOriginal, impuestos } = this.state;
    const totales = calcularImpuestosSobreBases(
      0,
      costoOriginal,
      impuestos,
      this.props.tipoDeCambio
    );

    this.setState(
      {
        costo: costoOriginal,
        costoImpuestos: totales.costoConImpuestos,
        fechaCostoSeleccionado: null,
      },
      () => {
        this.recalcularTodo();
        message.info("Costo restaurado al valor actual");
      }
    );
  };

  // --------------------------------------------------------------- cierre

  cerrarVerInsumo = (precio) => {
    this.setState({
      descripcion: "",
      descripcionLarga: "",
      clave: "",
      unidad: "",
      esEquivalencia: false,
      factorEquivalencia: 1,
      descripcionPrincipal: "",
      unidadPrincipal: "",
      costo: 0,
      costoOriginal: 0,
      costoImpuestos: 0,
      fechaCostoSeleccionado: null,
      moneda: "MXN",
      impuestos: [],
      calcularPreciosSobre: false,
      preciosInsumo: [],
      listaDePrecios: [],
      porcentaje: 0,
      precioCalculado: 0,
      precioCalculadoConImpuestos: 0,
      historialCostos: [],
      verHistorialCostos: false,
      cargandoInsumo: false,
      cargandoPrecios: false,
    });
    this.props.cerrarVerInsumo(precio);
  };

  elegirPrecio = (precio) => {
    if (!precio || precio <= 0) return;
    this.cerrarVerInsumo(precio);
    message.success(`Elegiste: ${dineroDisabledMask(precio)}`);
  };

  // --------------------------------------------------------------- render

  // `moneda` es opcional: solo las cifras de dinero la llevan.
  renderCifra = (etiqueta, valor, color, moneda) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {etiqueta}
      </span>
      <strong style={{ fontSize: 17, color: color || "#111827", lineHeight: 1.2 }}>
        {valor}
        {moneda ? <EtiquetaMoneda moneda={moneda} /> : null}
      </strong>
    </div>
  );

  renderTituloSeccion = (texto, extra) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "18px 0 8px 0",
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {texto}
      </span>
      {extra}
    </div>
  );

  renderBanda() {
    const {
      descripcionLarga,
      clave,
      unidad,
      esEquivalencia,
      factorEquivalencia,
      descripcionPrincipal,
      unidadPrincipal,
      costo,
      costoOriginal,
      costoImpuestos,
      moneda,
      impuestos,
      fechaCostoSeleccionado,
    } = this.state;

    const historico = !!fechaCostoSeleccionado;
    const colorCosto = historico ? COLOR_HISTORICO : "#111827";

    const resumenImpuestos =
      impuestos.length === 0
        ? "Sin impuestos"
        : impuestos
            .map(
              (imp, idx) =>
                `${nombreImpuesto(imp, idx)} ${etiquetaImpuesto(
                  imp,
                  this.props.tipoDeCambio
                )}`
            )
            .join(" · ");

    return (
      <div
        style={{
          background: historico ? "#f9f0ff" : `${COLOR_MARCA}15`,
          border: `1px solid ${historico ? COLOR_HISTORICO : COLOR_MARCA}`,
          borderRadius: 8,
          padding: "12px 16px",
          marginTop: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {clave ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{clave}</span>
          ) : null}
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: COLOR_MARCA,
              background: "#fff",
              border: `1px solid ${COLOR_MARCA}55`,
              borderRadius: 4,
              padding: "1px 7px",
            }}
          >
            {unidad}
          </span>
          {esEquivalencia && (
            // Se muestra el factor y la division que de verdad hace el backend
            // (costo del insumo / factor). No se escribe "1 CAJA = 12 PZA" porque la
            // direccion no es demostrable desde el codigo: la calculadora de Editar Insumo
            // rotula la equivalencia como la unidad MAYOR, pero el costo se DIVIDE entre el
            // factor, que implica la menor. Afirmar una de las dos seria adivinar.
            <Tooltip
              title={`El costo mostrado es el del insumo dividido entre ${factorEquivalencia}`}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#fff",
                  background: COLOR_MARCA,
                  borderRadius: 4,
                  padding: "1px 7px",
                  cursor: "help",
                }}
              >
                Equivalencia · factor {factorEquivalencia}
              </span>
            </Tooltip>
          )}
        </div>

        {esEquivalencia && descripcionPrincipal ? (
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
            Equivalencia de:{" "}
            <strong style={{ color: "#374151" }}>{descripcionPrincipal}</strong>
            {unidadPrincipal ? (
              <span style={{ color: "#9ca3af" }}> · unidad base {unidadPrincipal}</span>
            ) : null}
          </div>
        ) : null}

        {descripcionLarga ? (
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{descripcionLarga}</div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 36,
            flexWrap: "wrap",
            marginTop: 12,
            alignItems: "flex-start",
          }}
        >
          {this.renderCifra(
            historico ? "Costo base (hist.)" : "Costo base",
            dineroDisabledMask(costo),
            colorCosto,
            moneda
          )}
          {this.renderCifra(
            "Costo neto",
            dineroDisabledMask(costoImpuestos),
            colorCosto,
            moneda
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span
              style={{
                fontSize: 10,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Impuestos
            </span>
            <span style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>
              {resumenImpuestos}
            </span>
          </div>
        </div>

        {historico && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop: `1px dashed ${COLOR_HISTORICO}55`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              fontSize: 12,
            }}
          >
            <span style={{ color: "#6b7280" }}>Costo actual real:</span>
            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
              {dineroDisabledMask(costoOriginal)}
            </span>
            {/* Una sola etiqueta para el renglon: los dos importes estan en la misma
                moneda y repetirla en la Diferencia solo satura la nota al pie. */}
            <EtiquetaMoneda moneda={moneda} style={{ marginLeft: 0 }} />
            <span style={{ color: COLOR_HISTORICO, fontWeight: 600 }}>
              Diferencia {dineroDisabledMask(round2Decimals(costo - costoOriginal))}
            </span>
            <Tooltip title="Regresar al costo actual">
              <span
                onClick={this.restaurarCostoOriginal}
                style={{ color: COLOR_HISTORICO, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                <RollbackOutlined style={{ marginRight: 4 }} />
                Restaurar
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }

  renderPrecios() {
    const {
      listaDePrecios,
      preciosInsumo,
      costo,
      moneda,
      impuestos,
      calcularPreciosSobre,
      porcentaje,
    } = this.state;

    if (!listaDePrecios || listaDePrecios.length === 0) {
      return (
        <>
          {this.renderTituloSeccion("Precios configurados")}
          <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
            Esta empresa no tiene listas de precios configuradas
          </div>
        </>
      );
    }

    const filas = construirFilasDePrecios({
      listasEmpresa: listaDePrecios,
      preciosInsumo,
      costo,
      impuestos,
      calcularPreciosSobre,
      tipoDeCambio: this.props.tipoDeCambio,
    });

    const desfasados = filas.filter((f) => f.desfasado).length;

    return (
      <>
        {this.renderTituloSeccion(
          "Precios configurados",
          desfasados > 0 ? (
            <span style={{ fontSize: 11, color: "#b45309" }}>
              {desfasados} no corresponde{desfasados === 1 ? "" : "n"} al costo actual
            </span>
          ) : null
        )}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {filas.map((fila) => (
            <TarjetaPrecio
              key={fila.precioId}
              fila={fila}
              moneda={moneda}
              enCalculadora={Number(porcentaje) === Number(fila.porcentajeObjetivo)}
              esListaDelDocumento={
                !!this.props.precioId &&
                fila.precioId?.toString() === this.props.precioId.toString()
              }
              onUsar={this.elegirPrecio}
              onCargarSugerido={this.cargarSugeridoEnCalculadora}
            />
          ))}
        </div>
      </>
    );
  }

  renderCalculadora() {
    const {
      calcularPreciosSobre,
      atajosPorcentaje,
      porcentaje,
      precioCalculado,
      precioCalculadoConImpuestos,
      moneda,
    } = this.state;

    // Los configura la empresa en Configuracion > Insumos; sin nada capturado
    // caen a los de por defecto.
    const atajos = normalizarAtajosPorcentaje(atajosPorcentaje);

    return (
      <>
        {this.renderTituloSeccion(
          "Calculadora",
          <span style={{ fontSize: 11, color: "#9ca3af" }}>
            el % se aplica sobre{" "}
            <strong style={{ color: "#6b7280" }}>
              {calcularPreciosSobre ? "el costo" : "la venta"}
            </strong>
          </span>
        )}

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 12,
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: "1 1 240px", minWidth: 220 }}>
            <div style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>
              Porcentaje
            </div>
            <InputNumberItem
              value={porcentaje}
              onChange={this.onChangePorcentaje}
              min={-100}
              max={1000}
              step={0.5}
              porcentaje
              placeholder="Ej. 20"
            />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {atajos.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => this.onChangePorcentaje(p)}
                  style={{
                    border: `1px solid ${
                      Number(porcentaje) === p ? COLOR_MARCA : "#e5e7eb"
                    }`,
                    background: Number(porcentaje) === p ? `${COLOR_MARCA}15` : "#fff",
                    color: Number(porcentaje) === p ? COLOR_MARCA : "#6b7280",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 10px",
                    cursor: "pointer",
                  }}
                >
                  +{p}%
                </button>
              ))}
              <button
                type="button"
                onClick={() => this.onChangePorcentaje(0)}
                style={{
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  color: "#9ca3af",
                  borderRadius: 4,
                  fontSize: 11,
                  padding: "3px 10px",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "flex-end" }}>
            {this.renderCifra(
              "Precio (sin impuestos)",
              dineroDisabledMask(precioCalculado),
              COLOR_MARCA,
              moneda
            )}
            {this.renderCifra(
              "Precio neto",
              dineroDisabledMask(precioCalculadoConImpuestos),
              "#111827",
              moneda
            )}
            <ButtonItem
              type="primary"
              disabled={!precioCalculado || precioCalculado <= 0}
              onClick={() => this.elegirPrecio(precioCalculado)}
            >
              Elegir precio
            </ButtonItem>
          </div>
        </div>
      </>
    );
  }

  render() {
    const {
      descripcion,
      historialCostos,
      verHistorialCostos,
      fechaCostoSeleccionado,
      moneda,
      cargandoInsumo,
      cargandoPrecios,
    } = this.state;

    const cargando = cargandoInsumo || cargandoPrecios;
    // Sin datos todavia no se pinta el cuerpo: si no, se alcanza a leer "sin impuestos" y
    // "sin listas de precios" durante el primer render, que es justo lo contrario de lo que
    // dira el modal medio segundo despues. El alto fijo evita que salte al llenarse.
    const sinDatos = !descripcion;

    return (
      <ModalItem
        title={descripcion ? `Ver insumo — ${descripcion}` : "Ver insumo"}
        visible={this.props.visible}
        onCancel={() => this.cerrarVerInsumo()}
        width={1100}
        spinning={cargando}
      >
        {sinDatos ? (
          <div
            style={{
              minHeight: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9ca3af",
              fontSize: 12,
            }}
          >
            {cargando ? "" : "No se pudo cargar la informacion del insumo"}
          </div>
        ) : (
          <>
            {this.renderBanda()}
            {this.renderPrecios()}
            {this.renderCalculadora()}
            <HistorialCostos
              historial={historialCostos}
              moneda={moneda}
              abierto={verHistorialCostos}
              onToggle={() => this.setState({ verHistorialCostos: !verHistorialCostos })}
              fechaSeleccionada={fechaCostoSeleccionado}
              onUsar={this.usarCostoHistorial}
            />
          </>
        )}
      </ModalItem>
    );
  }
}

export default VerInsumo;
